import { ButtonHTMLAttributes, FunctionComponent } from 'react';
import styled from 'styled-components';
import { Colors } from 'src/utils/color';

// Helper function to darken color for border
const getBorderColor = (bgColor: string | undefined, defaultColor: string): string => {
    if (!bgColor) return Colors.theme.primary_dark;
    // If custom background color is provided, use it or calculate darker version
    // For now, if it's the primary color, use primary_dark
    if (bgColor === Colors.theme.primary || bgColor === '#257DE6') {
        return Colors.theme.primary_dark;
    }
    // For other colors, try to darken them slightly
    // Simple darkening: reduce RGB values by ~15%
    return bgColor; // Fallback to same color if we can't calculate
};

const StyledButton = styled.button<ButtonProps>`
    border: ${(props) => {
        if (props.border) return props.border;
        const bgColor = props.backgroundColor || Colors.theme.primary;
        const borderColor = props.borderColor || getBorderColor(bgColor, Colors.theme.primary_dark);
        return `1.5px solid ${borderColor}`;
    }};
    border-radius: ${(props) => props.borderRadius || '8px'};
    font-size: ${(props) => `${props.fontSize || 13}px`};
    font-weight: ${(props) => props.fontWeight || '500'};
    padding: ${(props) => props.padding || '10px 20px'};
    width: ${(props) => props.width};
    height: ${(props) => props.height};
    ${(props) =>
        props.disabled
            ? `
               color: ${props.color || '#a4aaaf'};
               background-color: ${props.backgroundColor || '#E5E7EB'};
               border-color: ${props.borderColor || '#D1D5DB'};
           `
            : `
               color: ${props.color || '#fff'};
               background-color: ${props.backgroundColor || Colors.theme.primary};
               border-color: ${props.borderColor || getBorderColor(props.backgroundColor, Colors.theme.primary_dark)};
               cursor: pointer;
               transition: all 0.2s ease;
               
               &:hover {
                   opacity: 0.9;
               }
               
               &:active {
                   opacity: 0.8;
               }
           `}
    ${(props) =>
        props.isBoxShadow
            ? `box-shadow: ${props.boxShadow || '0px 2px 0px #5a3dbf'};`
            : ``};
    margin: ${(props) => props.margin};
    background: ${(props) => props.background};
    
    /* Make SVG icons inherit the button's text color */
    svg {
        fill: currentColor;
        stroke: currentColor;
    }
`;

interface Props {
    children?: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    color?: string;
    backgroundColor?: string;
    fontSize?: number;
    width?: string;
    height?: string;
    padding?: string;
    isBoxShadow?: boolean;
    borderRadius?: string;
    borderColor?: string;
    className?: string;
    align?: 'left' | 'center' | 'right';
    fontWeight?: number;
    margin?: string;
    background?: string;
    type?: 'button' | 'submit' | 'reset' | undefined;
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    width?: string;
    height?: string;
    padding?: string;
    isBoxShadow?: boolean;
    borderRadius?: string;
    fontSize?: number;
    border?: string;
    backgroundColor?: string;
    borderColor?: string;
    boxShadow?: string;
    disabled?: boolean;
    fontWeight?: number;
    margin?: string;
    background?: string;
    type?: 'button' | 'submit' | 'reset' | undefined;
}

const Button: FunctionComponent<Props> = ({
    children,
    onClick,
    disabled,
    color,
    backgroundColor,
    fontSize,
    width,
    height,
    padding,
    isBoxShadow,
    borderRadius,
    borderColor,
    className,
    fontWeight,
    margin,
    background,
    type
}: Props) => {
    return (
        <StyledButton
            onClick={onClick}
            disabled={disabled}
            color={color}
            backgroundColor={backgroundColor}
            fontSize={fontSize}
            fontWeight={fontWeight}
            width={width}
            height={height}
            padding={padding}
            isBoxShadow={isBoxShadow}
            borderRadius={borderRadius}
            borderColor={borderColor}
            className={className}
            margin={margin}
            background={background}
            type={type}
        >
            {children}
        </StyledButton>
    );
};

export default Button;
